import {
  forwardRef, useId, useImperativeHandle, useRef, useState,
  type DragEvent, type InputHTMLAttributes, type ReactNode,
} from 'react'
import { AlertCircle, Upload } from 'lucide-react'
import { Meter } from './Meter'
import { Spinner } from './Spinner'
import { cx } from './cx'
import s from './FilePicker.module.css'

/**
 * Where the slot's file stands AFTER it was chosen (#2406, the drop zone's
 * states). `idle` takes a file; `uploading` is sending one and takes no other;
 * `error` says the last one did not land, and takes the next attempt.
 * Drag-over is not here: it is the pointer's state, and the slot tracks it.
 */
export type FilePickerStatus = 'idle' | 'uploading' | 'error'

export interface FilePickerProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value' | 'defaultValue' | 'size' | 'children' | 'title'> {
  /**
   * What was chosen or dropped. The input is cleared afterwards, so choosing
   * the SAME file again fires again — the second attempt after a refusal is the
   * common one, and an input that stays set answers it with silence.
   */
  onFiles: (files: File[]) => void
  /** The zone's line, worded by the caller: "Drop an SVG here". */
  title?: string
  /** The words of the real control inside the zone. */
  chooseLabel?: string
  /** What the slot accepts, after the control: "max 512 KB". */
  hint?: string
  /** What the slot holds NOW — a logo, an icon — drawn where the glyph would be. */
  preview?: ReactNode
  /** The upload's state, owned by the HOST that performs it. Default `idle`. */
  status?: FilePickerStatus
  /** Percent sent, 0–100, while `uploading`. Absent means indeterminate: a
   *  spinner, never a bar that pretends to know how far along it is. */
  progress?: number
  /** The line for `uploading` ("Uploading contract.pdf") or `error` ("The file
   *  could not be stored. Try again."). Worded by the host. */
  message?: ReactNode
}

/**
 * The kit's file slot (#2406), drawn to the leading design
 * (`temp-design/Moonbase Input Controls and Validation.html`, "Registration
 * document"): a RECESSED slot with an upload glyph, "Drop a file here", and
 * "or choose a file — max …" where *choose a file* is the real control.
 *
 * ── WHY A SLOT, NOT A BUTTON ────────────────────────────────────────────────
 * The design's one rule: a control that HOLDS a value is a slot cut into the
 * slab; a control that PERFORMS an action is a block raised out of it. A file
 * field holds a value — the logo, the icon — so it is a recess, and its states
 * are the value slot's states (`Input.module.css`): hover lightens one step,
 * focus is the ring plus the vein along the bottom cut, invalid swaps the vein
 * for crimson (Field adds the glyph and message), disabled goes flat. A file
 * input has no read-only state, so the slot has none either.
 *
 * ── THE INPUT STAYS THE CONTROL ────────────────────────────────────────────
 * It is visually hidden, NOT `display: none`: it keeps the tab stop, it is what
 * a `<Field>` label names (`id` and the aria props land on it, which is why
 * they pass straight through), Space/Enter opens the platform's picker, and
 * `setInputFiles` still reaches it. *choose a file* is a real `<label>` for it,
 * so a click there is the browser's own activation rather than a script
 * imitating one. Dropping onto the slot is an ENHANCEMENT over the same
 * `onFiles` — drag and drop alone reaches no keyboard, no screen reader and no
 * touch device, which `KbArticleAssets`' attach zone already says.
 */
export const FilePicker = forwardRef<HTMLInputElement, FilePickerProps>(function FilePicker(
  {
    onFiles, title = 'Drop a file here', chooseLabel = 'choose a file', hint, preview,
    status = 'idle', progress, message,
    className, disabled, id, ...rest
  },
  ref,
) {
  const input = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => input.current as HTMLInputElement)
  const auto = useId()
  const inputId = id ?? auto
  const hintId = `${inputId}-hint`
  const messageId = `${inputId}-message`
  const uploading = status === 'uploading'
  const failed = status === 'error'
  const describedBy = [
    rest['aria-describedby'],
    hint && !uploading ? hintId : null,
    message != null && status !== 'idle' ? messageId : null,
  ].filter(Boolean).join(' ') || undefined
  const [over, setOver] = useState(false)
  /* While a file is being sent the slot takes no other: a second choice would
     race the first, and the host has one upload to report on. The input keeps
     its tab stop (a disabled input would drop the focus on the floor mid-send)
     and says it is unavailable instead. */
  const refusing = disabled || uploading
  const percent = typeof progress === 'number' && Number.isFinite(progress)
    ? Math.min(100, Math.max(0, progress))
    : null

  const drop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setOver(false)
    if (refusing) return
    const files = Array.from(e.dataTransfer?.files ?? [])
    if (files.length > 0) onFiles(files)
  }

  return (
    /* The drag handlers are the ENHANCEMENT the header describes, not the
       control: a drop has no keyboard or screen-reader form to give this box,
       and the one that exists — the input — is inside it and reachable. A role
       here would claim an interaction the element does not have. */
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cx(s.slot, className)}
      data-file-picker=""
      data-over={(over && !refusing) || undefined}
      data-disabled={disabled || undefined}
      data-state={status === 'idle' ? undefined : status}
      aria-busy={uploading || undefined}
      onDragOver={(e) => { e.preventDefault(); if (!refusing) setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      {uploading
        ? (
          <>
            {/* The message is what is announced; the spinner's own "Loading" region
                would be a second, vaguer one. */}
            {percent == null && <span aria-hidden="true"><Spinner size={20} /></span>}
            <b className={s.title} id={messageId}>{message ?? 'Uploading…'}</b>
            {percent != null && (
              <div className={s.progress} role="progressbar" aria-labelledby={messageId}
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
                <Meter segments={[{ value: percent }]} max={100} height={6} />
              </div>
            )}
          </>
        )
        : (
          <>
            {preview ?? <Upload className={s.glyph} aria-hidden />}
            <b className={s.title}>{title}</b>
            <span className={s.sub}>
              or <label htmlFor={inputId} className={s.choose}>{chooseLabel}</label>
              {hint ? <> — <span id={hintId}>{hint}</span></> : null}
            </span>
            {/* The failure is ANNOUNCED when it appears and stays in the input's
                description, with a glyph. The zone is still ready for the next
                attempt, so the choose line above it stays too. */}
            {failed && message != null && (
              <span className={s.error} id={messageId} role="alert">
                <AlertCircle className={s.errorGlyph} aria-hidden />
                <span>{message}</span>
              </span>
            )}
          </>
        )}
      <input
        {...rest}
        id={inputId}
        ref={input}
        type="file"
        className={s.native}
        disabled={disabled}
        aria-disabled={uploading || undefined}
        aria-invalid={failed ? true : rest['aria-invalid']}
        aria-describedby={describedBy}
        onClick={(e) => { if (uploading) e.preventDefault(); rest.onClick?.(e) }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          e.target.value = ''
          if (files.length > 0 && !refusing) onFiles(files)
        }}
      />
    </div>
  )
})
